import React, { useState } from 'react';
import { Container, Stack, Title, Text, Card, Grid, Button, Group, Stepper, TextInput, NumberInput, Select, Textarea, Checkbox, FileInput, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconHome, IconMapPin, IconCurrencyDollar, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';

const AddListing = () => {
  const { user } = useAuth();
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: {
      // Basic Information
      title: '',
      description: '',
      propertyType: '',
      listingType: '',
      
      // Location
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'USA',
      
      // Pricing
      price: '',
      originalPrice: '',
      
      // Property Details
      bedrooms: '',
      bathrooms: '',
      halfBaths: '',
      squareFeet: '',
      lotSize: '',
      lotSizeUnit: 'Acres',
      yearBuilt: '',
      
      // Outdoor Features
      garageSpaces: '',
      parkingSpaces: '',
      pool: false,
      hotTub: false,
      deck: false,
      patio: false,
      firepit: false,
      outdoorKitchen: false,
      
      // Utilities
      heating: '',
      cooling: '',
      waterSource: '',
      sewer: '',
      electricity: '',
      internet: '',
      
      // Wilderness Features
      wildlifeViewing: false,
      hikingTrails: false,
      hunting: false,
      fishing: false,
      atvTrails: false,
      horseProperty: false,
      timberValue: false,
      mineralRights: false,
      
      // Agent Information
      listingAgent: '',
      listingAgentPhone: '',
      listingAgentEmail: ''
    },
    validate: {
      title: (value) => (!value ? 'Title is required' : null),
      propertyType: (value) => (!value ? 'Property type is required' : null),
      listingType: (value) => (!value ? 'Listing type is required' : null),
      address: (value) => (!value ? 'Address is required' : null),
      city: (value) => (!value ? 'City is required' : null),
      state: (value) => (!value ? 'State is required' : null),
      zipCode: (value) => (!value ? 'ZIP code is required' : null),
      price: (value) => (!value ? 'Price is required' : null),
    }
  });

  const handleSubmit = async (values) => {
    if (!user) {
      notifications.show({
        title: 'Authentication required',
        message: 'Please log in to add a listing',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/properties', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (response.ok) {
        notifications.show({
          title: 'Success!',
          message: 'Your property listing has been created',
          color: 'green',
        });
        form.reset();
        setActive(0);
      } else {
        notifications.show({
          title: 'Error',
          message: data.message || 'Failed to create listing',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Network error. Please try again.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Container size="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Authentication Required" color="red">
          Please log in to add a property listing.
        </Alert>
      </Container>
    );
  }

  const steps = [
    { label: 'Basic Info', icon: <IconHome size={16} /> },
    { label: 'Location', icon: <IconMapPin size={16} /> },
    { label: 'Pricing & Details', icon: <IconCurrencyDollar size={16} /> },
    { label: 'Features', icon: <IconCheck size={16} /> }
  ];

  const nextStep = () => setActive((current) => (current < 3 ? current + 1 : current));
  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  return (
    <Container size="xl">
      <Stack gap="xl">
        <Title order={1} c="green">Add Property Listing</Title>
        
        <Stepper active={active} onStepClick={setActive} breakpoint="sm">
          {steps.map((step, index) => (
            <Stepper.Step key={index} label={step.label} icon={step.icon} />
          ))}
        </Stepper>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Card shadow="sm" padding="xl" radius="md" withBorder>
            {active === 0 && (
              <Stack gap="md">
                <Title order={3}>Basic Information</Title>
                
                <TextInput
                  label="Property Title"
                  placeholder="Beautiful Mountain Cabin with Stunning Views"
                  required
                  {...form.getInputProps('title')}
                />
                
                <Select
                  label="Property Type"
                  placeholder="Select property type"
                  required
                  data={[
                    { value: 'House', label: 'House' },
                    { value: 'Condo', label: 'Condo' },
                    { value: 'Townhouse', label: 'Townhouse' },
                    { value: 'Land', label: 'Land' },
                    { value: 'Cabin', label: 'Cabin' },
                    { value: 'Ranch', label: 'Ranch' },
                    { value: 'Farm', label: 'Farm' },
                    { value: 'Other', label: 'Other' }
                  ]}
                  {...form.getInputProps('propertyType')}
                />
                
                <Select
                  label="Listing Type"
                  placeholder="Select listing type"
                  required
                  data={[
                    { value: 'For Sale', label: 'For Sale' },
                    { value: 'For Rent', label: 'For Rent' },
                    { value: 'For Lease', label: 'For Lease' },
                    { value: 'Auction', label: 'Auction' }
                  ]}
                  {...form.getInputProps('listingType')}
                />
                
                <Textarea
                  label="Description"
                  placeholder="Describe your property in detail..."
                  rows={4}
                  {...form.getInputProps('description')}
                />
              </Stack>
            )}

            {active === 1 && (
              <Stack gap="md">
                <Title order={3}>Location Information</Title>
                
                <TextInput
                  label="Address"
                  placeholder="123 Mountain View Road"
                  required
                  {...form.getInputProps('address')}
                />
                
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="City"
                      placeholder="Aspen"
                      required
                      {...form.getInputProps('city')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="State"
                      placeholder="CO"
                      required
                      {...form.getInputProps('state')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <TextInput
                      label="ZIP Code"
                      placeholder="81611"
                      required
                      {...form.getInputProps('zipCode')}
                    />
                  </Grid.Col>
                </Grid>
                
                <Select
                  label="Country"
                  data={[{ value: 'USA', label: 'United States' }]}
                  {...form.getInputProps('country')}
                />
              </Stack>
            )}

            {active === 2 && (
              <Stack gap="md">
                <Title order={3}>Pricing & Property Details</Title>
                
                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Price ($)"
                      placeholder="450000"
                      required
                      min={0}
                      {...form.getInputProps('price')}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Original Price ($)"
                      placeholder="500000"
                      min={0}
                      {...form.getInputProps('originalPrice')}
                    />
                  </Grid.Col>
                </Grid>
                
                <Grid>
                  <Grid.Col span={4}>
                    <NumberInput
                      label="Bedrooms"
                      placeholder="3"
                      min={0}
                      {...form.getInputProps('bedrooms')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <NumberInput
                      label="Bathrooms"
                      placeholder="2"
                      min={0}
                      step={0.5}
                      {...form.getInputProps('bathrooms')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <NumberInput
                      label="Half Baths"
                      placeholder="1"
                      min={0}
                      {...form.getInputProps('halfBaths')}
                    />
                  </Grid.Col>
                </Grid>
                
                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Square Feet"
                      placeholder="2500"
                      min={0}
                      {...form.getInputProps('squareFeet')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <NumberInput
                      label="Lot Size"
                      placeholder="5.5"
                      min={0}
                      step={0.1}
                      {...form.getInputProps('lotSize')}
                    />
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Select
                      label="Lot Size Unit"
                      data={[
                        { value: 'Acres', label: 'Acres' },
                        { value: 'Sq Ft', label: 'Sq Ft' },
                        { value: 'Sq Meters', label: 'Sq Meters' }
                      ]}
                      {...form.getInputProps('lotSizeUnit')}
                    />
                  </Grid.Col>
                </Grid>
                
                <NumberInput
                  label="Year Built"
                  placeholder="1995"
                  min={1800}
                  max={new Date().getFullYear()}
                  {...form.getInputProps('yearBuilt')}
                />
              </Stack>
            )}

            {active === 3 && (
              <Stack gap="md">
                <Title order={3}>Features & Amenities</Title>
                
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Text fw={500}>Outdoor Features</Text>
                      <Checkbox label="Pool" {...form.getInputProps('pool', { type: 'checkbox' })} />
                      <Checkbox label="Hot Tub" {...form.getInputProps('hotTub', { type: 'checkbox' })} />
                      <Checkbox label="Deck" {...form.getInputProps('deck', { type: 'checkbox' })} />
                      <Checkbox label="Patio" {...form.getInputProps('patio', { type: 'checkbox' })} />
                      <Checkbox label="Firepit" {...form.getInputProps('firepit', { type: 'checkbox' })} />
                      <Checkbox label="Outdoor Kitchen" {...form.getInputProps('outdoorKitchen', { type: 'checkbox' })} />
                    </Stack>
                  </Grid.Col>
                  
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <Text fw={500}>Wilderness Features</Text>
                      <Checkbox label="Wildlife Viewing" {...form.getInputProps('wildlifeViewing', { type: 'checkbox' })} />
                      <Checkbox label="Hiking Trails" {...form.getInputProps('hikingTrails', { type: 'checkbox' })} />
                      <Checkbox label="Hunting" {...form.getInputProps('hunting', { type: 'checkbox' })} />
                      <Checkbox label="Fishing" {...form.getInputProps('fishing', { type: 'checkbox' })} />
                      <Checkbox label="ATV Trails" {...form.getInputProps('atvTrails', { type: 'checkbox' })} />
                      <Checkbox label="Horse Property" {...form.getInputProps('horseProperty', { type: 'checkbox' })} />
                      <Checkbox label="Timber Value" {...form.getInputProps('timberValue', { type: 'checkbox' })} />
                      <Checkbox label="Mineral Rights" {...form.getInputProps('mineralRights', { type: 'checkbox' })} />
                    </Stack>
                  </Grid.Col>
                </Grid>
                
                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Garage Spaces"
                      placeholder="2"
                      min={0}
                      {...form.getInputProps('garageSpaces')}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Parking Spaces"
                      placeholder="4"
                      min={0}
                      {...form.getInputProps('parkingSpaces')}
                    />
                  </Grid.Col>
                </Grid>
                
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Heating"
                      placeholder="Forced Air, Radiant, etc."
                      {...form.getInputProps('heating')}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label="Cooling"
                      placeholder="Central Air, Window Units, etc."
                      {...form.getInputProps('cooling')}
                    />
                  </Grid.Col>
                </Grid>
                
                <Grid>
                  <Grid.Col span={4}>
                    <Select
                      label="Water Source"
                      placeholder="Select water source"
                      data={[
                        { value: 'City', label: 'City Water' },
                        { value: 'Well', label: 'Well' },
                        { value: 'Spring', label: 'Spring' },
                        { value: 'Other', label: 'Other' }
                      ]}
                      {...form.getInputProps('waterSource')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Select
                      label="Sewer"
                      placeholder="Select sewer type"
                      data={[
                        { value: 'City', label: 'City Sewer' },
                        { value: 'Septic', label: 'Septic' },
                        { value: 'Other', label: 'Other' }
                      ]}
                      {...form.getInputProps('sewer')}
                    />
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Select
                      label="Internet"
                      placeholder="Select internet type"
                      data={[
                        { value: 'High Speed', label: 'High Speed' },
                        { value: 'Satellite', label: 'Satellite' },
                        { value: 'Limited', label: 'Limited' },
                        { value: 'None', label: 'None' }
                      ]}
                      {...form.getInputProps('internet')}
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            )}

            <Group justify="space-between" mt="xl">
              <Button variant="default" onClick={prevStep} disabled={active === 0}>
                Previous
              </Button>
              
              {active === 3 ? (
                <Button type="submit" loading={loading} color="green">
                  Create Listing
                </Button>
              ) : (
                <Button type="button" onClick={nextStep}>Next</Button>
              )}
            </Group>
          </Card>
        </form>
      </Stack>
    </Container>
  );
};

export default AddListing;
